import axios from "axios";

// CRA only injects REACT_APP_* at build time; if .env is missing, avoid relative "undefined/api" URLs.
const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"
).replace(/\/$/, "");

/** Max product image upload size (KB); keep in sync with backend `PRODUCT_IMAGE_MAX_KB`. */
const _maxKb = parseInt(process.env.REACT_APP_PRODUCT_IMAGE_MAX_KB || "512", 10);
export const PRODUCT_IMAGE_MAX_KB = Number.isFinite(_maxKb) && _maxKb > 0 ? _maxKb : 512;
export const PRODUCT_IMAGE_MAX_BYTES = PRODUCT_IMAGE_MAX_KB * 1024;

export function backendOrigin() {
  return BACKEND_URL;
}

/** Resolve stored paths like `/uploads/...` to a full URL; pass through http(s) URLs. */
export function publicAssetUrl(maybePathOrUrl) {
  if (!maybePathOrUrl) return "";
  const s = String(maybePathOrUrl).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `${BACKEND_URL}${s.startsWith("/") ? s : `/${s}`}`;
}

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("scaffoindia_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const SESSION_EXPIRED_EVENT = "scaffoindia-session-expired";

export function clearAuthStorage() {
  localStorage.removeItem("scaffoindia_token");
  localStorage.removeItem("scaffoindia_user");
  localStorage.removeItem("scaffoindia_refresh_token");
}

// Endpoints where a 401 is a real answer (bad credentials etc.), not an expired session.
const NO_REFRESH_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/register", "/auth/buyer/register", "/auth/supplier-magic-login"];

let refreshPromise = null;

async function refreshAccessToken() {
  const body = {};
  const stored = localStorage.getItem("scaffoindia_refresh_token");
  if (stored) body.refresh_token = stored;
  // Plain axios (not `api`) so this call never re-enters the interceptor below.
  // withCredentials lets the HttpOnly refresh cookie travel when the API is on another origin.
  const res = await axios.post(`${BACKEND_URL}/api/auth/refresh`, body, { withCredentials: true });
  const { token, refresh_token: refreshToken, user } = res.data || {};
  if (!token) throw new Error("Refresh response missing token");
  localStorage.setItem("scaffoindia_token", token);
  if (refreshToken) localStorage.setItem("scaffoindia_refresh_token", refreshToken);
  if (user) localStorage.setItem("scaffoindia_user", JSON.stringify(user));
  return token;
}

// On 401: renew the access token once via the refresh session and retry the request.
// If renewal fails, clear the cached session and tell AuthProvider so the user is sent to login
// instead of every action failing silently until a page reload.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error || {};
    if (!config || !response || response.status !== 401 || config._authRetried) throw error;
    const url = String(config.url || "");
    if (NO_REFRESH_PATHS.some((p) => url.includes(p))) throw error;
    if (!localStorage.getItem("scaffoindia_token")) throw error;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
    } catch {
      clearAuthStorage();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      throw error;
    }
    config._authRetried = true;
    // The request interceptor re-reads the new token from localStorage on retry.
    return api(config);
  },
);

export default api;

async function postMultipartUpload(path, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await api.post(path, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  if (!res.data?.path) throw new Error("Upload response missing path");
  return res.data.path;
}

export function uploadAdminProductImage(file) {
  return postMultipartUpload("/admin/product-images", file);
}

export function uploadSupplierProductImage(file) {
  return postMultipartUpload("/supplier/product-images", file);
}

export async function verifyGst(gstin) {
  const res = await api.get("/gst/verify", { params: { gstin } });
  return res.data;
}
